import type { JSX } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { api, mediaUrl } from '../api';
import type { T } from '../i18n';
import { errorText, getState, set, toast } from '../state';
import type { AppState } from '../state';
import type { Postcard } from '../types';

/** The pile of postcards that have arrived: flick to shuffle, tap to open. */
export function Mailbox({ s, t }: { s: AppState; t: T }) {
  const cards = s.mailbox;
  if (cards.length === 0) {
    return (
      <div class="panel panel-mailbox">
        <div class="pile-stage">
          <div class="empty pile-empty">{t('mailboxEmpty')}</div>
        </div>
      </div>
    );
  }

  const n = cards.length;
  const top = ((s.pile % n) + n) % n;
  const next = () => set({ pile: (top + 1) % n });

  return (
    <div class="panel panel-mailbox">
      <div class="pile-stage">
        <div class="pile">
          {cards.map((card, i) => (
            <PileCard key={card.id} card={card} pos={(i - top + n) % n} onFlick={next} t={t} />
          ))}
        </div>
      </div>
      <button class="pile-count" onClick={next}>
        {top + 1} / {n} →
      </button>
    </div>
  );
}

function PileCard({ card, pos, onFlick, t }: { card: Postcard; pos: number; onFlick: () => void; t: T }) {
  const [dx, setDx] = useState(0);
  const drag = useRef<{ x: number } | null>(null);
  const isTop = pos === 0;

  // Keyed by card, each element persists as the pile moves, so every card
  // glides to its new place in the stack rather than being redrawn there.
  const transform = isTop
    ? `translate(${dx}px, 0) rotate(${(dx / 20).toFixed(2)}deg)`
    : `translate(${pos * 4}px, ${pos * 11}px) scale(${1 - pos * 0.03}) rotate(${pos % 2 ? 1.5 : -1.7}deg)`;

  const down = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    drag.current = { x: e.clientX };
    setDx(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (drag.current) setDx(e.clientX - drag.current.x);
  };
  const up = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const d = e.clientX - drag.current.x;
    drag.current = null;
    setDx(0);
    // Generous thresholds, so the gesture still works with a thumb.
    if (Math.abs(d) > 55) onFlick();
    else if (Math.abs(d) < 6) openCard(card);
  };
  const cancel = () => {
    drag.current = null;
    setDx(0);
  };

  return (
    <div
      class="pc"
      data-sealed={String(card.unread)}
      data-photo={String(!!card.frontMedia)}
      data-dragging={String(drag.current !== null)}
      style={{
        transform,
        zIndex: 40 - pos,
        opacity: pos > 2 ? 0 : 1,
        pointerEvents: isTop ? 'auto' : 'none',
        transition: drag.current ? 'none' : undefined,
      }}
      role="button"
      tabIndex={isTop ? 0 : -1}
      aria-hidden={isTop ? undefined : 'true'}
      aria-label={`${card.place} · ${card.from}`}
      onPointerDown={isTop ? down : undefined}
      onPointerMove={isTop ? move : undefined}
      onPointerUp={isTop ? up : undefined}
      onPointerCancel={isTop ? cancel : undefined}
      onKeyDown={
        isTop
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openCard(card);
              }
            }
          : undefined
      }
    >
      <div class="pc-front">
        {card.frontMedia && <img src={mediaUrl(card.frontMedia)} alt="" loading="lazy" draggable={false} />}
        {card.frontMedia && <div class="pc-scrim" />}
        <span class="pc-label">{card.frontLabel}</span>
        <div class="pc-caption">
          <div class="pc-place">{card.place}</div>
          <div class="pc-meta">
            {card.from} · {card.postmark}
          </div>
        </div>
        {card.unread && <span class="pc-new">{t('newBadge')}</span>}
      </div>
    </div>
  );
}

/** Opening a card shows its front, then turns it over a beat later, as in the design. */
export function openCard(card: Postcard): void {
  set({ openCardId: card.id, cardSide: 'front' });
  window.setTimeout(() => {
    const s = getState();
    if (s.openCardId === card.id && s.cardSide === 'front') set({ cardSide: 'back' });
  }, 340);

  if (!card.unread) return;
  api
    .markRead(card.id)
    .then(() => {
      set({ mailbox: getState().mailbox.map((c) => (c.id === card.id ? { ...c, unread: false } : c)) });
    })
    .catch((err) => toast(errorText(err, 'No pudimos marcarla como leída.'), 'error'));
}
